/**
 * Tipo do salto entre a versão que o bundle carrega e a que está publicada.
 * É o que a `UpdatePolicy` roteia — patch e minor caem na mesma camada.
 */
export type ReleaseType = 'patch' | 'minor' | 'major'

type ParsedVersion = [major: number, minor: number, patch: number]

/**
 * Aceita `X.Y.Z` com as folgas que o mundo real produz: prefixo `v` das tags de
 * git, sufixo de prerelease (`-rc.1`) e metadados de build (`+81db40e`). Tudo
 * que não couber nesse formato é entrada desconhecida, não versão.
 */
const VERSION_PATTERN = /^v?(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/

const parseVersion = (value: unknown): ParsedVersion | null => {
  if (typeof value !== 'string') return null

  const match = VERSION_PATTERN.exec(value.trim())

  if (!match) return null

  return [Number(match[1]), Number(match[2]), Number(match[3])]
}

/**
 * Diz que tipo de atualização separa a versão local da publicada.
 *
 * Fail-closed por design, e a razão está num incidente concreto: o mecanismo
 * anterior comparava commit sha, tratava qualquer diferença como "recarregue
 * agora", e em 29/07/2026 recarregou o app de todo participante a cada 60s em
 * produção (PR #85), derrubando missões em andamento. Aqui, qualquer entrada
 * que não seja semver conhecido dos dois lados devolve `null` — e `null` nunca
 * chega a `location.reload()`.
 *
 * Client à frente do servidor também é `null`: acontece em preview, em rollback
 * e em cache de CDN servindo um bundle mais novo que o deploy corrente.
 * Recarregar aí faria o app voltar no tempo, sem nada a ganhar.
 */
export const diffVersions = (
  current: unknown,
  latest: unknown
): ReleaseType | null => {
  const parsedCurrent = parseVersion(current)
  const parsedLatest = parseVersion(latest)

  if (!parsedCurrent || !parsedLatest) return null

  const [currentMajor, currentMinor, currentPatch] = parsedCurrent
  const [latestMajor, latestMinor, latestPatch] = parsedLatest

  if (latestMajor !== currentMajor)
    return latestMajor > currentMajor ? 'major' : null

  if (latestMinor !== currentMinor)
    return latestMinor > currentMinor ? 'minor' : null

  if (latestPatch !== currentPatch)
    return latestPatch > currentPatch ? 'patch' : null

  return null
}
